import { ORG_MEMBERS_READ_PERMISSION } from "./org-members.js";
import { z } from "zod";
//#region src/platform-permission-request.ts
/**
* The shape of a platform permission key: `platform:<area>:<verb>`, three lowercase segments. Every
* platform key in the tree has this shape (`platform:delegation:mint`, `platform:agent-capability:issue`,
* {@link ORG_MEMBERS_READ_PERMISSION}). The JSON schema's `platformPermissions` items carry the same
* pattern, and the CP to DP wire member is bound by it and by nothing narrower, so a key added to the
* allowlist later still reconciles on an operator that predates it (00-index §1.2).
*/
const PLATFORM_PERMISSION_KEY_PATTERN = /^platform:[a-z][a-z0-9-]*:[a-z][a-z0-9-]*$/;
/**
* The platform permission that lets an app create groups and manage the groups it creates
* (2026-09-30-agent-streams-and-app-owned-groups, decision 2). An app names it in `app.json`
* `platformPermissions[]`; an org admin consents to it once per organization on the app's detail
* page. With consent, the app's brokered service bearer may `POST /v1/admin/groups`, and core-api
* writes the app principal's `group:manage` row on the new group in the same transaction. The key
* gives the app no reach over a group it did not create; that takes a `group:manage` grant.
*
* The area is `groups`, plural, the same word as the org keys `groups:read` and `groups:manage`
* (`research/name-collisions.md` row 5). The resource type is `group`, singular, as in
* `group:manage` (`RESOURCE_TYPE_CATALOG`).
*
* Readers: release-engine's publish gate (through {@link isRequestablePlatformPermissionKey}),
* core-api's consent routes and the group-create guard (WS-4), and this module's catalog.
*/
const PLATFORM_GROUPS_MANAGE_PERMISSION = "platform:groups:manage";
/**
* The platform permissions an app may request. Adding a key here is a product decision per key
* (overview §7): the publish gate admits it, the consent routes accept it and the wizard shows its
* label. Every member is `platform:`-prefixed, so `isReservedOrgPermissionKey` refuses it as an
* app-defined key and it can never be declared in `permissions[]`.
*
* Two keys: {@link ORG_MEMBERS_READ_PERMISSION} (R-020 of 2026-09-07-org-members-read-for-apps) and
* {@link PLATFORM_GROUPS_MANAGE_PERMISSION} (decision 2 of 2026-09-30-agent-streams-and-app-owned-groups).
* The order is the order they were admitted; no reader depends on it.
*/
const REQUESTABLE_PLATFORM_PERMISSION_KEYS = [ORG_MEMBERS_READ_PERMISSION, PLATFORM_GROUPS_MANAGE_PERMISSION];
/**
* The catalog behind {@link REQUESTABLE_PLATFORM_PERMISSION_KEYS}, keyed by the key so a consumer
* looks a label up in one step and the type refuses a key with no entry. core-api folds `label` and
* `description` into `AppPlatformPermissionRequest` on `GET /v1/admin/apps-catalog/:appId`; the shell
* renders the wire copy and never imports this table.
*/
const REQUESTABLE_PLATFORM_PERMISSIONS = {
	[ORG_MEMBERS_READ_PERMISSION]: {
		key: ORG_MEMBERS_READ_PERMISSION,
		label: "Read organization members",
		description: "List the members of each organization that onboards this app: email, name, active state, last login, resolved roles, group ids and each member's access to this app, plus pending invitations. Read-only, and no admin-only field leaves the platform."
	},
	[PLATFORM_GROUPS_MANAGE_PERMISSION]: {
		key: PLATFORM_GROUPS_MANAGE_PERMISSION,
		label: "Create and manage groups",
		description: "Create groups in each organization that onboards this app, and manage the groups it creates: add and remove any person or agent of the organization as a member, nest child groups, rename and delete them. The app gets no access to a group it did not create unless someone grants it."
	}
};
/** True when `key` is a member of {@link REQUESTABLE_PLATFORM_PERMISSION_KEYS}. */
function isRequestablePlatformPermissionKey(key) {
	return REQUESTABLE_PLATFORM_PERMISSION_KEYS.includes(key);
}
/**
* The zod form of the allowlist, for a route parameter or body field that names a requestable key
* (core-api's `PUT`/`DELETE /v1/admin/apps-catalog/:appId/platform-permissions/:key`). An unknown key
* is a 400 before any service runs.
*/
const requestablePlatformPermissionKeySchema = z.enum(REQUESTABLE_PLATFORM_PERMISSION_KEYS);
//#endregion
export { PLATFORM_GROUPS_MANAGE_PERMISSION, PLATFORM_PERMISSION_KEY_PATTERN, REQUESTABLE_PLATFORM_PERMISSIONS, REQUESTABLE_PLATFORM_PERMISSION_KEYS, isRequestablePlatformPermissionKey, requestablePlatformPermissionKeySchema };

//# sourceMappingURL=platform-permission-request.js.map